/**
 * Reading a building's detail response, and shaping it for the panel.
 *
 * `GET /api/v1/building/{local_id}` has no `response_model`, so openapi-typescript
 * can only describe it as `Record<string, unknown>`. The choice is between an `as`
 * cast — which tells the compiler to stop checking, and is exactly how a renamed
 * field becomes a blank panel — and narrowing once, here, where it can be tested.
 * `funnel/steps.ts` makes the same choice for the two report endpoints.
 *
 * The readers below deliberately differ from that file's: its `str` rejects the
 * empty string because an empty `checkout_url` would redirect to the current page,
 * whereas here an empty `uso` is a field with nothing in it and should read as
 * absent rather than as an error.
 *
 * The roadmap is the part worth testing. In the legacy page each step was built by
 * interpolating fifteen optional fields into an HTML string, so a missing one
 * reached the screen as the word "null" — visible only to whoever happened to
 * click a building whose district-heating step had no connection cost. Normalising
 * to a fixed shape here leaves the JSX no conditionals to get wrong.
 */

import { fmtEuro } from "./format";

// ── Narrowing ─────────────────────────────────────────────────────────────

function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

function num(value: unknown): number | null {
  // A number, not a numeric string: the pipeline emits JSON numbers, and coercing
  // "abc" to NaN and rendering that would be worse than showing nothing.
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function record(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function list(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(record) : [];
}

// ── The shapes the panel consumes ─────────────────────────────────────────

export interface Subsidy {
  nombre: string;
  cobertura: string | null;
  requisito: string | null;
  destinatario: string | null;
}

export interface RoadmapStep {
  step: number;
  /** Step 1 is fabric work and the rest are equipment. They read differently and
   *  the legacy page coloured them differently, so it belongs here as a fact
   *  about the step rather than a decision made at render time. */
  passive: boolean;
  title: string;
  descripcion: string | null;
  /** Short figures shown on one line: "−28% calefacción", "5.3 kW/vivienda". */
  facts: string[];
  /** Already formatted, because the unit depends on which field was present: a
   *  lump sum is "€", a district-heating connection is "€/vivienda". */
  cost: string;
  /** The one figure worth highlighting, if the step reports one. */
  saving: string | null;
  note: string | null;
}

export interface Recommendation {
  tecnologia: string | null;
  potenciaKwVivienda: number | null;
  potenciaKwEdificio: number | null;
  nViviendas: number | null;
  letraObjetivo: string | null;
  ahorroCosteAnual: number | null;
  ahorroEmisionesPct: number | null;
  roadmap: RoadmapStep[];
  ayudas: Subsidy[];
}

export interface BuildingDetail {
  localId: string | null;
  nomMun: string | null;
  uso: string | null;
  epoca: string | null;
  cluster: string | null;
  zonaClimatica: string | null;
  orientacion: string | null;
  superficieM2: number | null;
  alturas: number | null;
  nViviendas: number | null;
  letra: string | null;
  eedDeadline: string | null;
  epnrenM2: number | null;
  co2M2: number | null;
  heatM2: number | null;
  coolM2: number | null;
  costeAero: number | null;
  costeGeo: number | null;
  ahorroCoste: number | null;
  sistemaLabel: string | null;
  sistemaTypology: string | null;
  /** 'auto' when the systems were inferred from typology rather than declared.
   *  Surfaced, because an inferred system is a guess and the reader deserves to
   *  know which of the two they are looking at. */
  sistemaSource: string | null;
  recomendacion: Recommendation;
}

// ── Roadmap ───────────────────────────────────────────────────────────────

/**
 * The cost of a step, with whatever unit its own fields imply.
 *
 * Three different fields across the step types, checked in the order the legacy
 * page checked them: a fabric step carries `coste_estimado`, an equipment step
 * `coste_instalacion`, and a district-heating connection is quoted per dwelling.
 */
function stepCost(raw: Record<string, unknown>): string {
  const estimado = num(raw["coste_estimado"]);
  if (estimado !== null) return fmtEuro(estimado);

  const instalacion = num(raw["coste_instalacion"]);
  if (instalacion !== null) return fmtEuro(instalacion);

  const conexion = num(raw["coste_conexion_vivienda"]);
  if (conexion !== null) return `${fmtEuro(conexion)}/vivienda`;

  return "–";
}

/**
 * The saving a step claims, in whichever terms it reports one.
 *
 * Euros per year and a percentage of gas consumption are different claims, so the
 * label says which. Both absent is normal — replacing windows reports its effect
 * as reduced demand, which appears among the facts instead.
 */
function stepSaving(raw: Record<string, unknown>): string | null {
  const euros = num(raw["ahorro_anual_coste"]) ?? num(raw["ahorro_coste_anual"]);
  if (euros !== null) return `Ahorro estimado: ${fmtEuro(euros)}/año`;

  const pct = num(raw["ahorro_estimado_pct"]);
  if (pct !== null) return `Ahorro estimado: ${pct}% del consumo de gas`;

  return null;
}

/** The figures a step puts on one line, in reading order. */
function stepFacts(
  raw: Record<string, unknown>, passive: boolean, nViviendas: number | null,
  potenciaEdificio: number | null,
): string[] {
  if (passive) {
    // The reductions arrive as strings ("28%") from the estimator and are passed
    // through rather than reformatted: reformatting would mean parsing a display
    // string and guessing at its unit.
    const cal = str(raw["reduccion_calefaccion"]);
    const ref = str(raw["reduccion_refrigeracion"]);
    return [
      ...(cal ? [`−${cal} calefacción`] : []),
      ...(ref ? [`−${ref} refrigeración`] : []),
    ];
  }

  const kw = num(raw["potencia_kw_vivienda"]);
  const scop = num(raw["scop"]);
  const seer = num(raw["seer"]);
  return [
    ...(kw !== null ? [`${kw} kW/vivienda`] : []),
    // Only when there is more than one dwelling: "total edificio 5.3 kW" beside
    // "5.3 kW/vivienda" reads as a contradiction in a single-family house.
    ...(nViviendas !== null && nViviendas > 1 && potenciaEdificio !== null
      ? [`total edificio ${potenciaEdificio} kW`] : []),
    ...(scop !== null ? [`SCOP ${scop}`] : []),
    ...(seer !== null ? [`SEER ${seer}`] : []),
  ];
}

export function readRoadmap(rec: Record<string, unknown>): RoadmapStep[] {
  const nViviendas = num(rec["n_viviendas"]);
  const potenciaEdificio = num(rec["potencia_kw_edificio"]);

  return list(rec["roadmap"]).map((raw, index) => {
    // Falls back to the array position when `step` is missing, so a step is never
    // headed "Paso null" — one of the legacy failures this replaces.
    const step = num(raw["step"]) ?? index + 1;
    const passive = step === 1;
    return {
      step,
      passive,
      title: str(raw["tecnologia"]) ?? str(raw["tipo"]) ?? `Paso ${step}`,
      descripcion: str(raw["descripcion"]) ?? str(raw["tipo"]),
      facts: stepFacts(raw, passive, nViviendas, potenciaEdificio),
      cost: stepCost(raw),
      saving: stepSaving(raw),
      note: str(raw["nota"]),
    };
  });
}

function readSubsidies(rec: Record<string, unknown>): Subsidy[] {
  return list(rec["ayudas"])
    .map((raw) => ({
      nombre: str(raw["nombre"]),
      cobertura: str(raw["cobertura"]),
      requisito: str(raw["requisito"]),
      destinatario: str(raw["destinatario"]),
    }))
    // A subsidy with no name is not something anyone could go and apply for.
    .filter((s): s is Subsidy => s.nombre !== null);
}

// ── The whole response ────────────────────────────────────────────────────

export function readBuildingDetail(
  body: Record<string, unknown>,
): BuildingDetail {
  const rec = record(body["recomendacion"]);

  return {
    localId: str(body["localId"]),
    nomMun: str(body["nom_mun"]),
    uso: str(body["uso"]),
    epoca: str(body["epoca"]),
    cluster: str(body["cluster"]),
    zonaClimatica: str(body["zona_climatica"]),
    orientacion: str(body["orientacion"]),
    superficieM2: num(body["superficie_m2"]),
    alturas: num(body["alturas"]),
    nViviendas: num(body["n_viviendas"]),
    letra: str(body["letra_actual"]),
    eedDeadline: str(body["eed_deadline"]),
    epnrenM2: num(body["epnren_m2"]),
    co2M2: num(body["co2_m2"]),
    heatM2: num(body["heat_m2"]),
    coolM2: num(body["cool_m2"]),
    costeAero: num(body["coste_aero"]),
    costeGeo: num(body["coste_geo"]),
    ahorroCoste: num(body["ahorro_coste"]),
    sistemaLabel: str(body["sistema_label"]),
    sistemaTypology: str(body["sistema_typology"]),
    sistemaSource: str(body["sistema_source"]),
    recomendacion: {
      tecnologia: str(rec["tecnologia"]),
      potenciaKwVivienda: num(rec["potencia_kw_vivienda"]),
      potenciaKwEdificio: num(rec["potencia_kw_edificio"]),
      nViviendas: num(rec["n_viviendas"]),
      letraObjetivo: str(rec["letra_objetivo"]),
      ahorroCosteAnual: num(rec["ahorro_coste_anual"]),
      ahorroEmisionesPct: num(rec["ahorro_emisiones_pct"]),
      roadmap: readRoadmap(rec),
      ayudas: readSubsidies(rec),
    },
  };
}

/**
 * Whether this building has an energy assessment at all.
 *
 * Industrial and agricultural buildings carry geometry and no demand, and the
 * panel says so rather than printing a column of en dashes. Mirrors the legacy
 * page's test exactly: EPnren or heating demand, either one.
 *
 * Worth knowing while reading this: a building with no data still carries
 * `letra_actual`, and it is "A" — the artifact that rates 37 335 buildings top of
 * the scale for having had nothing measured. This flag is what stops the panel
 * presenting that as an assessment.
 */
export function hasEnergyData(d: BuildingDetail): boolean {
  return d.epnrenM2 !== null || d.heatM2 !== null;
}

/**
 * The Catastro facade photo, through our own proxy.
 *
 * The legacy page pointed an <img> straight at ovc.catastro.meh.es, which meant
 * every reader's browser announced itself to a government service and the CSP had
 * to allow that host in `img-src`. The proxy already exists for the funnel, sits
 * behind the shared circuit breaker, and lets `img-src` stay at 'self'.
 */
export function facadePhotoUrl(localId: string): string {
  return `/api/v1/catastro/fachada/${encodeURIComponent(localId)}`;
}

/**
 * The heat-study link for this building.
 *
 * `local_id` is what makes a response attributable, and the study form derives
 * both the municipality and the street address from it — so this one parameter is
 * the whole of that path's provenance.
 */
export function studyLink(localId: string): string {
  return `/estudio-calor?local_id=${encodeURIComponent(localId)}`;
}
